import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
// eslint-disable-next-line jira/restricted/@atlaskit+flag
import type { CreateFlagArgs, DismissFn } from '@atlaskit/flag';
import type { IntlShape } from '@atlassian/jira-intl';
import type {
	Form,
	SelectOption,
	UnsavedForm,
} from '@atlassian/proforma-common-core/form-system-models';
import { FormStore } from '@atlassian/proforma-common-core/form-system-stores';
import {
	AFFECTED_SERVICES_SCHEMA_CUSTOM,
	CMDB_SCHEMA_CUSTOM,
	getCmdbFieldValueMap,
	LABELS_SCHEMA_CUSTOM,
	LABELS_SCHEMA_SYSTEM,
} from '@atlassian/proforma-common-core/form-system-utils';
import type {
	ApiError,
	ApiFormChoicesResponse,
	PermissionLevel,
} from '@atlassian/proforma-common-core/jira-common-models';
import {
	loadChoicesAsyncFn,
	loadChoicesFn,
	loadFieldDataFn,
} from '@atlassian/proforma-common-core/jira-common-stores';
import type { ErrorUtils } from '@atlassian/proforma-common-core/jira-common-utils';
import type { IssueViewFormApi } from '../apis/issue-view-form-api.tsx';
import type { IssueViewModuleContext } from '../ui/issue-view-forms/types.tsx';
import { messages } from './messages.tsx';

export class IssueViewStore {
	public readonly issueKey: string;

	public readonly issueId: number;

	public readonly projectId: number;

	private readonly _loadFieldDataFn: (
		formId?: number,
	) => (formStore: FormStore) => () => Promise<void>;

	private readonly _loadChoicesFn: (
		formStore: FormStore,
	) => (questionId?: number, query?: string) => Promise<void>;

	private readonly _loadChoicesAsyncFn: (
		formStore: FormStore,
	) => (questionId: number, query: string) => Promise<SelectOption[]>;

	private readonly issueViewFormApi: IssueViewFormApi;

	private readonly errorUtils: ErrorUtils;

	private readonly permissions: PermissionLevel;

	private readonly createAnalyticsEvent: CreateUIAnalyticsEvent;

	private readonly editModeCallback: (isFormInEditMode: boolean) => void;

	public constructor(
		private readonly intl: IntlShape,
		moduleContext: IssueViewModuleContext,
		projectId: number,
		issueKey: string,
		issueId: number,
		createAnalyticsEvent: CreateUIAnalyticsEvent,
		private readonly clearFormSaveErrors: () => void,
		editModeCallback: (isFormInEditMode: boolean) => void,
		showFlag: (args: CreateFlagArgs) => DismissFn,
	) {
		this.issueKey = issueKey;
		this.issueId = issueId;
		this.projectId = projectId;
		this.editModeCallback = editModeCallback;
		this._loadFieldDataFn = (formId) =>
			loadFieldDataFn(
				(request) =>
					(formId && moduleContext.apis.issueViewFormApi.postFieldData(formId, request)) ||
					Promise.resolve({}),
				moduleContext.utils.errorUtils,
			);
		this._loadChoicesFn = loadChoicesFn(
			(templateFormId: number, formId?: number): Promise<ApiFormChoicesResponse> =>
				(formId && this.issueViewFormApi.getFormChoices(formId)) || Promise.reject(),
			moduleContext.utils.errorUtils,
			true,
			createAnalyticsEvent,
		);

		this._loadChoicesAsyncFn = loadChoicesAsyncFn(
			{
				custom: {
					[AFFECTED_SERVICES_SCHEMA_CUSTOM]: {
						searchOptionsFn: (_, query): Promise<SelectOption[]> =>
							moduleContext.apis.issueViewFormApi.searchAffectedServices(query),
					},
					[CMDB_SCHEMA_CUSTOM]: {
						searchOptionsFn: (jiraField, query, issueFieldData, form): Promise<SelectOption[]> =>
							(form &&
								form.formId &&
								issueFieldData?.fieldConfigId &&
								moduleContext.apis.issueViewFormApi.searchCmdb(
									jiraField,
									query,
									this.issueId,
									form.formId,
									issueFieldData.fieldConfigId,
									getCmdbFieldValueMap(form),
								)) ||
							Promise.resolve([]),
						onSearchError: ({ status }: ApiError) => {
							if (status === 400) {
								return {
									title: intl.formatMessage(messages.objectSearchErrorTitle),
									description: intl.formatMessage(messages.objectSearchErrorDescription),
								};
							}
						},
					},
					[LABELS_SCHEMA_CUSTOM]: {
						searchOptionsFn: (jiraField, query): Promise<SelectOption[]> =>
							(!query.includes(' ') &&
								moduleContext.apis.issueViewFormApi.searchLabels(issueId, jiraField, query)) ||
							Promise.resolve([]),
					},
				},
				system: {
					[LABELS_SCHEMA_SYSTEM]: {
						searchOptionsFn: (jiraField, query): Promise<SelectOption[]> =>
							(!query.includes(' ') &&
								moduleContext.apis.issueViewFormApi.searchLabels(issueId, jiraField, query)) ||
							Promise.resolve([]),
					},
				},
			},
			moduleContext.utils.errorUtils,
			createAnalyticsEvent,
			showFlag,
		);

		this.issueViewFormApi = moduleContext.apis.issueViewFormApi;
		this.errorUtils = moduleContext.utils.errorUtils;
		this.permissions = moduleContext.permissions;
		this.createAnalyticsEvent = createAnalyticsEvent;
	}

	private searchUsers = (jiraField: string | undefined, query: string): Promise<SelectOption[]> =>
		this.issueViewFormApi.searchUsers(jiraField, query);

	private searchUsersWithFieldConfig = (
		jiraField: string | undefined,
		fieldConfigId: string | undefined,
		query: string,
	): Promise<SelectOption[]> =>
		this.issueViewFormApi.searchUsersWithFieldConfig(
			jiraField,
			fieldConfigId,
			this.projectId,
			query,
		);

	public callAddForm = async (templateFormId: number) =>
		this.issueViewFormApi.addForm(this.issueId, templateFormId);

	public loadFormStore = async (formId: number): Promise<FormStore> => {
		try {
			return this.issueViewFormApi.getForm(formId).then((form) => this.createFormStore(form));
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			this.errorUtils.reportError(error, {
				id: 'loadFormStore',
				attributes: { formId },
			});
			return Promise.reject(error);
		}
	};

	public createFormStore = (form: Form | UnsavedForm): FormStore =>
		new FormStore(
			this.intl,
			this.createAnalyticsEvent,
			this._loadFieldDataFn(form.id).bind(this),
			this._loadChoicesFn.bind(this),
			this._loadChoicesAsyncFn.bind(this),
			this.searchUsers.bind(this),
			this.searchUsersWithFieldConfig.bind(this),
			form,
			false,
		);
}
